import { createAsyncThunk } from '@reduxjs/toolkit'
import { taskTrackerApi } from '../../api/taskTracker.api'
import {
  mergeBoardColumnsOrderFromApi,
  mergeBoardItemsOrderFromApi,
} from '../../api/taskTracker.api.mappers'
import {
  extractColumnsFromBoardItems,
  getBoardLaneItemKey,
  sortBoardItemsByPosition,
} from '../../lib/taskTrackerBoardItems.utils'
import { buildTaskTrackerBoardMoveItems } from '../../lib/taskTrackerBoardMove.utils'
import { DEFAULT_BOARD_COLUMN_COLOR } from '../../lib/boardColumn.constants'
import type {
  MoveTaskTrackerBoardItemType,
  TaskTrackerBoardLaneItem,
} from '../domain.types'
import { getErrorMessage } from '../shared/thunk.utils'
import type { RootState } from '@store/redux.store'

export const fetchBoardEditDataThunk = createAsyncThunk(
  'taskTracker/fetchBoardEditData',
  async (boardId: number, { rejectWithValue }) => {
    try {
      const details = await taskTrackerApi.getBoard(boardId)
      return { boardId, details }
    } catch (error: unknown) {
      return rejectWithValue({
        boardId,
        error: getErrorMessage(error, 'Не удалось загрузить данные доски'),
      })
    }
  },
)

export const saveBoardTitleThunk = createAsyncThunk(
  'taskTracker/saveBoardTitle',
  async (
    payload: { boardId: number; boardTitle: string },
    { rejectWithValue },
  ) => {
    try {
      if (!payload.boardTitle.trim()) {
        return rejectWithValue('Название доски не может быть пустым')
      }
      const updatedBoard = await taskTrackerApi.updateBoard(payload.boardId, {
        title: payload.boardTitle,
      })
      return { boardId: payload.boardId, board: updatedBoard }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось сохранить доску'))
    }
  },
)

export const saveBoardColumnThunk = createAsyncThunk(
  'taskTracker/saveBoardColumn',
  async (
    payload: { boardId: number; columnId: number; title: string; color: string },
    { rejectWithValue },
  ) => {
    try {
      if (!payload.title.trim()) {
        return rejectWithValue('Название колонки не может быть пустым')
      }
      const updatedColumn = await taskTrackerApi.updateColumn(payload.columnId, {
        title: payload.title,
        color: payload.color,
      })
      return { boardId: payload.boardId, column: updatedColumn }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось сохранить колонку'))
    }
  },
)

export const deleteBoardColumnThunk = createAsyncThunk(
  'taskTracker/deleteBoardColumn',
  async (
    payload: { boardId: number; columnId: number },
    { rejectWithValue },
  ) => {
    try {
      await taskTrackerApi.deleteColumn(payload.columnId)
      return payload
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось удалить колонку'))
    }
  },
)

export const deleteBoardSummaryThunk = createAsyncThunk(
  'taskTracker/deleteBoardSummary',
  async (
    payload: { boardId: number; summaryId: number },
    { rejectWithValue },
  ) => {
    try {
      const result = await taskTrackerApi.deleteSummary(payload.summaryId)
      if (!result.deleted) {
        return rejectWithValue('Не удалось удалить сводку')
      }

      return payload
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось удалить сводку'))
    }
  },
)

export const reorderBoardLanesThunk = createAsyncThunk(
  'taskTracker/reorderBoardLanes',
  async (
    payload: {
      boardId: number
      activeType: MoveTaskTrackerBoardItemType
      activeId: number
      overType: MoveTaskTrackerBoardItemType
      overId: number
      orderedItems: Array<{ id: number; type: MoveTaskTrackerBoardItemType }>
    },
    { getState, rejectWithValue },
  ) => {
    const state = getState() as RootState & {
      taskTracker: {
        boardEdit: {
          byBoardId: Record<number, { boardItems: TaskTrackerBoardLaneItem[] }>
        }
      }
    }
    const editState = state.taskTracker.boardEdit.byBoardId[payload.boardId]
    const boardItems = editState?.boardItems ?? []

    if (
      payload.activeType === payload.overType &&
      payload.activeId === payload.overId
    ) {
      return null
    }

    const itemsByKey = new Map(
      boardItems.map((item) => [getBoardLaneItemKey(item), item]),
    )
    const reorderedBoardItems = payload.orderedItems
      .map((item) => itemsByKey.get(`${item.type}:${item.id}`))
      .filter(Boolean)
      .map((item, index) => ({
        ...(item as TaskTrackerBoardLaneItem),
        position: index + 1,
      }))

    if (reorderedBoardItems.length !== boardItems.length) {
      return rejectWithValue('Не удалось определить новый порядок столбцов')
    }

    const previousBoardItems = boardItems

    try {
      const updatedBoardItems = await taskTrackerApi.moveColumns(
        payload.boardId,
        buildTaskTrackerBoardMoveItems(reorderedBoardItems),
      )

      const previousColumns = extractColumnsFromBoardItems(previousBoardItems)
      const updatedColumns = extractColumnsFromBoardItems(updatedBoardItems)

      return {
        boardId: payload.boardId,
        previousBoardItems,
        updatedBoardItems: mergeBoardItemsOrderFromApi(
          previousBoardItems,
          updatedBoardItems,
        ),
        updatedColumns: mergeBoardColumnsOrderFromApi(
          previousColumns,
          updatedColumns,
        ),
      }
    } catch (error: unknown) {
      return rejectWithValue({
        boardId: payload.boardId,
        previousBoardItems,
        error: getErrorMessage(error, 'Не удалось изменить порядок столбцов'),
      })
    }
  },
)

/** @deprecated используйте reorderBoardLanesThunk */
export const reorderBoardColumnsThunk = reorderBoardLanesThunk

export const createBoardColumnThunk = createAsyncThunk(
  'taskTracker/createBoardColumn',
  async (
    payload: { boardId: number; title: string; color: string },
    { rejectWithValue },
  ) => {
    try {
      if (!payload.title.trim()) {
        return rejectWithValue('Название колонки не может быть пустым')
      }
      const createdColumn = await taskTrackerApi.createColumn(payload.boardId, {
        title: payload.title,
        color: payload.color,
      })
      return { boardId: payload.boardId, column: createdColumn }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось создать колонку'))
    }
  },
)

export const deleteBoardThunk = createAsyncThunk(
  'taskTracker/deleteBoard',
  async (
    payload: { boardId: number; password: string },
    { rejectWithValue },
  ) => {
    try {
      const result = await taskTrackerApi.deleteBoard(
        payload.boardId,
        payload.password,
      )
      return { boardId: payload.boardId, deleted: result.deleted }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось удалить доску'))
    }
  },
)

export { DEFAULT_BOARD_COLUMN_COLOR }
